import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { Dialog } from '@/components/AppDialog'
import { useCreateVehicle, useOutlets, useStaff, useUpdateVehicle } from '@/api/hooks'
import type { Vehicle } from '@/api/types'
import { ErrorBanner } from '@/components/common'

const TYPES = ['VAN', 'TEMPO', 'TRUCK', 'CAR', 'TWO_WHEELER', 'REFRIGERATED']
const OWNERSHIP = ['OWNED', 'HIRED', 'VENDOR']

/**
 * Puts a vehicle on the register, or corrects one already there.
 *
 * <p>The driver is chosen from the staff master rather than typed: a name in a
 * box is a name nobody can ring, and the person who drives is on the payroll
 * with a licence and a number already recorded against them. Only those whose
 * designation is driver are offered - a steward in the list is a steward
 * somebody will eventually pick.</p>
 */
export function VehicleDialog({
  open,
  vehicle,
  onClose,
}: {
  open: boolean
  /** The vehicle being corrected. Absent, a new one is registered. */
  vehicle?: Vehicle | null
  onClose: () => void
}) {
  const create = useCreateVehicle()
  const update = useUpdateVehicle()
  const { data: outlets } = useOutlets()
  const { data: drivers } = useStaff({ role: 'DRIVER' })
  const editing = Boolean(vehicle)

  const [registrationNumber, setRegistrationNumber] = useState('')
  const [vehicleType, setVehicleType] = useState('VAN')
  const [ownership, setOwnership] = useState('OWNED')
  const [outletId, setOutletId] = useState('')
  const [vendorName, setVendorName] = useState('')
  const [capacityKg, setCapacityKg] = useState('')
  const [capacityDescription, setCapacityDescription] = useState('')
  const [defaultDriverStaffId, setDefaultDriverStaffId] = useState('')
  const [insuranceExpiry, setInsuranceExpiry] = useState('')
  const [fitnessExpiry, setFitnessExpiry] = useState('')
  const [permitExpiry, setPermitExpiry] = useState('')
  const [costPerTrip, setCostPerTrip] = useState('')
  const [costPerKm, setCostPerKm] = useState('')

  useEffect(() => {
    if (!open) return
    setRegistrationNumber(vehicle?.registrationNumber ?? '')
    setVehicleType(vehicle?.vehicleType ?? 'VAN')
    setOwnership(vehicle?.ownership ?? 'OWNED')
    setOutletId(vehicle?.outletId ?? '')
    setVendorName(vehicle?.vendorName ?? '')
    setCapacityKg(vehicle?.capacityKg != null ? String(vehicle.capacityKg) : '')
    setCapacityDescription(vehicle?.capacityDescription ?? '')
    setDefaultDriverStaffId(vehicle?.defaultDriverStaffId ?? '')
    setInsuranceExpiry(vehicle?.insuranceExpiry ?? '')
    setFitnessExpiry(vehicle?.fitnessExpiry ?? '')
    setPermitExpiry(vehicle?.permitExpiry ?? '')
    setCostPerTrip(vehicle?.costPerTrip != null ? String(vehicle.costPerTrip) : '')
    setCostPerKm(vehicle?.costPerKm != null ? String(vehicle.costPerKm) : '')
  }, [open, vehicle?.id])

  const submit = () => {
    const body = {
      registrationNumber: registrationNumber.trim().toUpperCase(),
      vehicleType,
      ownership,
      outletId: outletId || undefined,
      vendorName: vendorName.trim() || undefined,
      capacityKg: capacityKg ? Number(capacityKg) : undefined,
      capacityDescription: capacityDescription.trim() || undefined,
      defaultDriverStaffId: defaultDriverStaffId || undefined,
      insuranceExpiry: insuranceExpiry || undefined,
      fitnessExpiry: fitnessExpiry || undefined,
      permitExpiry: permitExpiry || undefined,
      costPerTrip: costPerTrip ? Number(costPerTrip) : undefined,
      costPerKm: costPerKm ? Number(costPerKm) : undefined,
    }
    if (vehicle) {
      update.mutate({ id: vehicle.id, body }, { onSuccess: onClose })
      return
    }
    create.mutate(body, { onSuccess: onClose })
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {editing ? `Vehicle ${vehicle?.registrationNumber}` : 'Register a vehicle'}
      </DialogTitle>
      <DialogContent dividers>
        <ErrorBanner error={create.error ?? update.error} />
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Registration number"
              value={registrationNumber}
              onChange={(event) => setRegistrationNumber(event.target.value)}
              fullWidth
              required
              placeholder="TN 37 BX 4412"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              label="Type"
              value={vehicleType}
              onChange={(event) => setVehicleType(event.target.value)}
              fullWidth
            >
              {TYPES.map((value) => (
                <MenuItem key={value} value={value}>
                  {value.replace('_', ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase())}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              select
              label="Ownership"
              value={ownership}
              onChange={(event) => setOwnership(event.target.value)}
              fullWidth
            >
              {OWNERSHIP.map((value) => (
                <MenuItem key={value} value={value}>
                  {value.toLowerCase().replace(/^./, (c) => c.toUpperCase())}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              label="Outlet"
              value={outletId}
              onChange={(event) => setOutletId(event.target.value)}
              fullWidth
              helperText="Left blank, it serves the whole business"
            >
              <MenuItem value="">Any outlet</MenuItem>
              {(outlets ?? []).map((outlet) => (
                <MenuItem key={outlet.id} value={outlet.id}>
                  {outlet.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {ownership !== 'OWNED' ? (
            <Grid item xs={12}>
              <TextField
                label="Hired from"
                value={vendorName}
                onChange={(event) => setVendorName(event.target.value)}
                fullWidth
              />
            </Grid>
          ) : null}

          <Grid item xs={12}>
            <TextField
              select
              label="Usual driver"
              value={defaultDriverStaffId}
              onChange={(event) => setDefaultDriverStaffId(event.target.value)}
              fullWidth
              helperText={
                (drivers ?? []).length === 0
                  ? 'Nobody on the staff master is a driver yet'
                  : 'Filled in on an outpass, and can be changed there'
              }
            >
              <MenuItem value="">Not fixed</MenuItem>
              {(drivers ?? []).map((driver) => (
                <MenuItem key={driver.id} value={driver.id}>
                  {driver.fullName}
                  {driver.mobile ? ` · ${driver.mobile}` : ''}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Capacity (kg)"
              type="number"
              value={capacityKg}
              onChange={(event) => setCapacityKg(event.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="What it holds"
              value={capacityDescription}
              onChange={(event) => setCapacityDescription(event.target.value)}
              fullWidth
              placeholder="20 vessels and 4 counters"
            />
          </Grid>

          {/* The papers. A van whose insurance has lapsed cannot be sent out,
              and the register says so on the list rather than at the gate. */}
          <Grid item xs={12} md={4}>
            <TextField
              label="Insurance to"
              type="date"
              value={insuranceExpiry}
              onChange={(event) => setInsuranceExpiry(event.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Fitness to"
              type="date"
              value={fitnessExpiry}
              onChange={(event) => setFitnessExpiry(event.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Permit to"
              type="date"
              value={permitExpiry}
              onChange={(event) => setPermitExpiry(event.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Cost per trip"
              type="number"
              value={costPerTrip}
              onChange={(event) => setCostPerTrip(event.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Cost per km"
              type="number"
              value={costPerKm}
              onChange={(event) => setCostPerKm(event.target.value)}
              fullWidth
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!registrationNumber.trim() || create.isPending || update.isPending}
        >
          {editing ? 'Save' : 'Register'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
